import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useLazyQuery, useMutation} from '@apollo/client';
import {Button, Form, Input, InputNumber, Modal, Popconfirm, Spin, Table, message} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import {
    BCP_CHECK_JOB_CONFIG_VERSION_DETAIL,
    BCP_CHECK_JOB_CONFIG_VERSIONS_PAGE,
} from './graphql/queries/bcpCheckJobQueries';
import {ROLLBACK_BCP_CHECK_JOB_CONFIG_VERSION} from './graphql/mutations/bcpCheckJobMutations';
import {
    CheckJobConfigVersionDetailResponse,
    CheckJobConfigVersionsPageResponse,
    CheckJobDataSourceType,
    JobConfigVersionType,
} from './graphql/types/BcpCheckJobTypes';
import {formatTime} from './utils/timeFormat';

const BcpCheckJobVersionsPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const [inputJobId, setInputJobId] = useState<string>(id ?? '');
    const jobId = Number(id ?? inputJobId);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [versionModalOpen, setVersionModalOpen] = useState(false);
    const [dsModalOpen, setDsModalOpen] = useState(false);
    const [activeDsRecord, setActiveDsRecord] = useState<CheckJobDataSourceType | null>(null);

    const [doQuery, {data, loading}] =
        useLazyQuery<CheckJobConfigVersionsPageResponse>(BCP_CHECK_JOB_CONFIG_VERSIONS_PAGE);

    const fetchVersions = (page: number = currentPage, size: number = pageSize) => {
        doQuery({variables: {jobId: jobId || 0, pageRequest: {page, size}}});
    };

    useEffect(() => {
        if (data?.bcpCheckJobConfigVersionsPage?.pageInfo) {
            const pi = data.bcpCheckJobConfigVersionsPage.pageInfo;
            setTotalElements(pi.totalElements ?? 0);
            setCurrentPage(pi.page ?? 1);
            setPageSize(pi.size ?? 10);
        }
    }, [data]);

    useEffect(() => {
        if (id) {
            fetchVersions(1, pageSize);
        } else {
            fetchVersions(1, pageSize);
        }
    }, [id]);

    const [fetchDetail, {data: detailData, loading: detailLoading}] =
        useLazyQuery<CheckJobConfigVersionDetailResponse>(BCP_CHECK_JOB_CONFIG_VERSION_DETAIL);

    const [doRollback] = useMutation(ROLLBACK_BCP_CHECK_JOB_CONFIG_VERSION);

    const versionList = data?.bcpCheckJobConfigVersionsPage?.content ?? [];

    const detail = detailData?.bcpCheckJobConfigVersionDetail?.data;

    const handleViewDetail = (record: JobConfigVersionType) => {
        const jid = record.jobId || jobId;
        if (!jid) return;
        fetchDetail({variables: {jobId: jid, version: record.version}});
        setVersionModalOpen(true);
    };

    const handleRollback = async (record: JobConfigVersionType) => {
        const jid = record.jobId || jobId;
        if (!jid) return;
        try {
            const res = await doRollback({variables: {jobId: jid, version: record.version}});
            const result = res.data?.rollbackBcpCheckJobConfigVersion;
            if (!result?.success) {
                message.error(result?.message ?? '回滚失败');
                return;
            }
            message.success(`已回滚到版本 ${record.version}`);
            if (jid) {
                fetchVersions(currentPage);
            }
        } catch (e: any) {
            message.error(e?.message ?? '回滚失败');
        }
    };

    const handleClickDs = (record: CheckJobDataSourceType) => {
        setActiveDsRecord(record);
        setDsModalOpen(true);
    };

    const closeVersionModal = () => {
        setVersionModalOpen(false);
        setActiveDsRecord(null);
        setDsModalOpen(false);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
        fetchVersions(page, size);
    };

    const tableColumns: ColumnsType<JobConfigVersionType> = [
        {title: '任务ID', dataIndex: 'jobId', key: 'jobId', width: 100},
        {title: '版本号', dataIndex: 'version', key: 'version', width: 100},
        {title: '创建人', dataIndex: 'createUser', key: 'createUser', width: 160},
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 200,
            render: (t: string) => formatTime(t),
        },
        {
            title: '操作',
            key: 'action',
            width: 160,
            render: (_: any, record: JobConfigVersionType) => (
                <div className="flex gap-2">
                    <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
                        查看详情
                    </Button>
                    <Popconfirm
                        title={`确认回滚到版本 ${record.version}？`}
                        description="回滚后当前配置将被该版本覆盖。"
                        onConfirm={() => handleRollback(record)}
                        okText="确认"
                        cancelText="取消"
                    >
                        <Button type="link" size="small" danger>回滚</Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const dsTableColumns: ColumnsType<CheckJobDataSourceType> = [
        {title: '类型', key: 'type', width: 80, render: () => '数据库'},
        {
            title: '数据库',
            key: 'database',
            width: 150,
            render: (_: any, r: CheckJobDataSourceType) => {
                try { return JSON.parse(r.config ?? '{}').database ?? '-'; } catch { return '-'; }
            },
        },
        {title: '表名', dataIndex: 'tableName', key: 'tableName', width: 150},
        {title: '过滤规则', dataIndex: 'filterRule', key: 'filterRule', ellipsis: true},
    ];

    const handleSearch = () => {
        fetchVersions(1, pageSize);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-6 pb-6 pt-4">
            {/* 页头 */}
            <div className="flex items-center gap-3 mb-4">
                <h2 className="text-base font-semibold m-0">任务历史版本</h2>
            </div>

            {/* 查询区 */}
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <div className="flex items-center">
                    <span className="w-20 flex-shrink-0 text-right pr-2 text-sm">任务ID</span>
                    <Input
                        className="flex-1"
                        value={inputJobId}
                        onChange={(e) => setInputJobId(e.target.value)}
                        placeholder="请输入任务ID"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Button type="primary" onClick={handleSearch}>查询</Button>
                </div>
            </div>

            {/* 版本列表 */}
            <Table
                dataSource={versionList}
                columns={tableColumns}
                rowKey={(r) => String(r.version ?? r.id)}
                loading={loading}
                pagination={false}
            />
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />

            {/* 版本详情弹框 */}
            <Modal
                title="版本配置详情"
                open={versionModalOpen}
                onCancel={closeVersionModal}
                footer={<Button onClick={closeVersionModal}>关闭</Button>}
                width={760}
                destroyOnClose
            >
                {detailLoading ? (
                    <div className="flex justify-center py-8"><Spin size="large" /></div>
                ) : (
                    <Form layout="vertical" disabled>
                        <div className="max-w-2xl">
                            <Form.Item label="任务名称">
                                <Input value={detail?.name ?? ''} />
                            </Form.Item>
                            <Form.Item label="用户组">
                                <Input value={detail?.userGroupName ?? String(detail?.userGroupId ?? '')} />
                            </Form.Item>
                        </div>

                        <div className="mb-4 max-w-3xl">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">数据源列表</span>
                                <span className="text-xs text-gray-400">点击行查看详情</span>
                            </div>
                            <Table
                                dataSource={detail?.dataSources ?? []}
                                columns={dsTableColumns}
                                rowKey={(r) => String(r.id ?? Math.random())}
                                pagination={false}
                                size="small"
                                locale={{emptyText: '暂无数据源'}}
                                onRow={(record) => ({
                                    onClick: () => handleClickDs(record),
                                    style: {cursor: 'pointer'},
                                })}
                            />
                        </div>

                        <div className="max-w-2xl">
                            <Form.Item label="核对规则">
                                <Input.TextArea rows={3} value={detail?.checkRule ?? ''} />
                            </Form.Item>
                            <Form.Item label="窗口时间(秒)">
                                <InputNumber className="w-full" value={detail?.windowTime ?? undefined} />
                            </Form.Item>
                            <Form.Item label="告警接收人">
                                <Input value={detail?.alarmReceiver ?? ''} />
                            </Form.Item>
                            <Form.Item label="备注">
                                <Input.TextArea rows={2} value={detail?.remark ?? ''} />
                            </Form.Item>
                        </div>
                    </Form>
                )}
            </Modal>

            {/* 数据源详情弹框 */}
            <Modal
                title="数据源详情"
                open={dsModalOpen}
                onCancel={() => setDsModalOpen(false)}
                footer={<Button onClick={() => setDsModalOpen(false)}>关闭</Button>}
                width={600}
                destroyOnClose
            >
                {activeDsRecord && (
                    <Form layout="vertical" disabled>
                        <Form.Item label="数据源类型">
                            <Input value="数据库" />
                        </Form.Item>
                        <Form.Item label="必须存在">
                            <Input value={activeDsRecord.mustExist ? '是' : '否'} />
                        </Form.Item>
                        <Form.Item label="数据库名">
                            <Input value={(() => { try { return JSON.parse(activeDsRecord.config ?? '{}').database ?? ''; } catch { return ''; } })()} />
                        </Form.Item>
                        <Form.Item label="表名">
                            <Input value={activeDsRecord.tableName ?? ''} />
                        </Form.Item>
                        <Form.Item label="使用字段">
                            <Input.TextArea rows={2} value={activeDsRecord.useFields ?? ''} />
                        </Form.Item>
                        <Form.Item label="分组规则">
                            <Input.TextArea rows={2} value={activeDsRecord.groupRule ?? ''} />
                        </Form.Item>
                        <Form.Item label="过滤规则">
                            <Input.TextArea rows={2} value={activeDsRecord.filterRule ?? ''} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default BcpCheckJobVersionsPage;
